import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useToast } from '../../context/ToastContext';

const STATUS_FLOW = {
  DRAFT:     { next: 'GENERATED', label: 'Mark as Generated', color: 'bg-gray-100 text-gray-700' },
  GENERATED: { next: 'SENT',      label: 'Mark as Sent',      color: 'bg-blue-100 text-blue-700' },
  SENT:      { next: 'PAID',      label: 'Mark as Paid',      color: 'bg-yellow-100 text-yellow-700' },
  PAID:      { next: null,        label: null,                 color: 'bg-green-100 text-green-700' },
};

const STATUS_STEPS = ['DRAFT', 'GENERATED', 'SENT', 'PAID'];

const MMG_COMPONENTS = ['Per Month Overall MMG'];

const fmt = (val) => val != null ? `₹${parseFloat(val).toFixed(2)}` : '—';

const Invoices = ({ enterpriseId }) => {
  const toast = useToast();

  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState('');

  // Generate form state
  const [invoiceMonth, setInvoiceMonth] = useState('');
  const [additionalCharge, setAdditionalCharge] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, [enterpriseId]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/invoices/enterprise/${enterpriseId}`);
      setInvoices(response.data);
      setError('');
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to load billing records';
      setError(errMsg);
      toast.error(errMsg);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceMonth) { toast.error('Please select a month'); return; }

    // Block current and future months (YYYY-MM format)
    const now = new Date();
    const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (invoiceMonth >= currentYearMonth) {
      toast.error('Invoice can only be generated for completed past months.');
      return;
    }

    try {
      setGenerating(true);
      setError('');

      const payload = { enterpriseId, invoiceMonth };
      if (additionalCharge && parseFloat(additionalCharge) > 0) {
        payload.additionalCharge = parseFloat(additionalCharge);
      }

      await api.post(`/api/admin/invoices/generate`, payload);
      toast.success('Billing generated successfully');
      setInvoiceMonth('');
      setAdditionalCharge('');
      await fetchInvoices();
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to generate billing';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await api.get(`/api/admin/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setViewMode('detail');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load billing details');
    }
  };

  const handleBackToList = () => { setViewMode('list'); setSelectedInvoice(null); };

  const handleStatusChange = async () => {
    const nextStatus = STATUS_FLOW[selectedInvoice.status]?.next;
    if (!nextStatus) return;
    const confirmed = window.confirm(
      `Change billing status from ${selectedInvoice.status} → ${nextStatus}?\n\nThis action cannot be reversed.`
    );
    if (!confirmed) return;
    try {
      setUpdatingStatus(true);
      const response = await api.patch(`/api/admin/invoices/${selectedInvoice.id}/status`, { status: nextStatus });
      setSelectedInvoice(response.data);
      await fetchInvoices();
      toast.success(`Billing status updated to ${nextStatus}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update billing status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadgeColor = (status) => STATUS_FLOW[status]?.color || 'bg-gray-100 text-gray-700';

  if (loading) {
    return <div className="flex justify-center items-center py-8"><p className="text-gray-500">Loading billing records...</p></div>;
  }

  // ─── Detail View ────────────────────────────────────────────────────────────
  if (viewMode === 'detail' && selectedInvoice) {
    const currentStatusIndex = STATUS_STEPS.indexOf(selectedInvoice.status);
    const nextStatus = STATUS_FLOW[selectedInvoice.status]?.next;
    const nextLabel = STATUS_FLOW[selectedInvoice.status]?.label;

    return (
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={handleBackToList} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
            ← Back to List
          </button>
          <h2 className="text-xl font-semibold text-gray-800">
            Billing for {String(selectedInvoice.invoiceMonth).padStart(2, '0')}/{selectedInvoice.invoiceYear}
          </h2>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(selectedInvoice.status)}`}>
            {selectedInvoice.status}
          </span>
          {nextStatus ? (
            <button onClick={handleStatusChange} disabled={updatingStatus}
              className="ml-auto px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors whitespace-nowrap">
              {updatingStatus ? 'Updating...' : nextLabel}
            </button>
          ) : (
            <span className="ml-auto px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">✓ Fully Paid</span>
          )}
        </div>

        {/* Status Progress */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${idx <= currentStatusIndex ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                    {idx < currentStatusIndex ? '✓' : idx + 1}
                  </div>
                  <span className={`text-xs font-medium ${idx <= currentStatusIndex ? 'text-primary' : 'text-gray-400'}`}>{step}</span>
                </div>
                {idx < STATUS_STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${idx < currentStatusIndex ? 'bg-primary' : 'bg-gray-100'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Invoice Meta */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
            <div>
              <p className="text-xs text-gray-400 uppercase mb-1">Billing Number</p>
              <p className="text-lg font-semibold text-gray-800">{selectedInvoice.invoiceNumber}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase mb-1">Month / Year</p>
              <p className="text-lg font-semibold text-gray-800">
                {String(selectedInvoice.invoiceMonth).padStart(2, '0')}/{selectedInvoice.invoiceYear}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase mb-1">Total Amount</p>
              <p className="text-lg font-semibold text-gray-800">₹{parseFloat(selectedInvoice.totalAmount || 0).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase mb-1">Generated By</p>
              <p className="text-gray-700">{selectedInvoice.generatedByName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase mb-1">Generated On</p>
              <p className="text-gray-700">{new Date(selectedInvoice.generatedAt).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Line Items Table */}
          {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
            <div className="overflow-x-auto">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Charge Breakdown</h3>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-400 uppercase bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Component</th>
                    <th className="text-left px-4 py-3 font-medium whitespace-nowrap">Description</th>
                    <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Qty</th>
                    <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Unit</th>
                    <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Actual Calculated</th>
                    <th className="text-right px-4 py-3 font-medium whitespace-nowrap">MMG Floor</th>
                    <th className="text-right px-4 py-3 font-medium whitespace-nowrap">Billed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {selectedInvoice.lineItems.map((item) => {
                    const isMMG = MMG_COMPONENTS.includes(item.component);
                    const mmgApplied = item.mmgFloor != null && parseFloat(item.mmgFloor) > 0 &&
                                      parseFloat(item.actualCalculated) < parseFloat(item.mmgFloor);

                    return (
                      <tr key={item.id} className={`hover:bg-gray-50 ${isMMG ? 'bg-amber-50' : ''}`}>
                        {/* Component */}
                        <td className={`px-4 py-3 font-semibold text-sm whitespace-nowrap ${isMMG ? 'text-amber-700' : 'text-gray-800'}`}>
                          {item.component}
                        </td>
                        {/* Description */}
                        <td className="px-4 py-3 text-gray-500 text-xs max-w-xs">{item.description || '—'}</td>
                        {/* Qty */}
                        <td className="px-4 py-3 text-right text-gray-600 text-xs">
                          {item.quantity != null ? parseFloat(item.quantity).toLocaleString() : '—'}
                        </td>
                        {/* Unit Rate */}
                        <td className="px-4 py-3 text-right text-gray-600 text-xs">
                          {item.unitRate != null ? fmt(item.unitRate) : '—'}
                        </td>
                        {/* Actual Calculated */}
                        <td className="px-4 py-3 text-right text-gray-700">
                          {fmt(item.actualCalculated)}
                        </td>
                        {/* MMG Floor */}
                        <td className="px-4 py-3 text-right">
                          {item.mmgFloor != null && parseFloat(item.mmgFloor) > 0 ? (
                            <span className={`inline-flex items-center gap-1 font-medium ${mmgApplied ? 'text-amber-600' : 'text-gray-400'}`}>
                              {fmt(item.mmgFloor)}
                              {mmgApplied && <span title="MMG applied">⚡</span>}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        {/* Billed */}
                        <td className={`px-4 py-3 text-right font-bold ${isMMG ? 'text-amber-700' : 'text-gray-800'}`}>
                          {fmt(item.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                  <tr>
                    <td colSpan="6" className="px-4 py-4 text-right font-semibold text-gray-800 text-sm">
                      Total Amount to Pay
                    </td>
                    <td className="px-4 py-4 text-right font-bold text-lg text-gray-900">
                      ₹{parseFloat(selectedInvoice.totalAmount || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* MMG Legend */}
              <p className="mt-2 text-xs text-gray-400">
                ⚡ MMG applied — actual charge was below the minimum monthly guarantee floor
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── List View ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Generate Invoice Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Generate Billing</h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-sm text-red-600">{error}</p>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">×</button>
          </div>
        )}

        <form onSubmit={handleGenerateInvoice} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Billing Month <span className="text-red-500">*</span></label>
            <input
              type="month"
              value={invoiceMonth}
              onChange={(e) => setInvoiceMonth(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Charges <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={additionalCharge}
              onChange={(e) => setAdditionalCharge(e.target.value)}
              placeholder="₹0.00 — leave blank if none"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={generating}
              className="w-full px-6 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating...' : 'Generate Billing'}
            </button>
          </div>
        </form>
      </div>

      {/* Invoices List */}
      {invoices.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Recent Billing</p>
            <p className="text-xs text-gray-400">{invoices.length} billing record{invoices.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-400 uppercase bg-gray-50">
                <tr>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Billing Number</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Month/Year</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Status</th>
                  <th className="text-right px-5 py-3 font-medium whitespace-nowrap">Total Amount</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Generated By</th>
                  <th className="text-left px-5 py-3 font-medium whitespace-nowrap">Generated On</th>
                  <th className="text-center px-5 py-3 font-medium whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-700 font-medium">{invoice.invoiceNumber}</td>
                    <td className="px-5 py-3 text-gray-700">
                      {String(invoice.invoiceMonth).padStart(2, '0')}/{invoice.invoiceYear}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-700 font-semibold">
                      ₹{parseFloat(invoice.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-gray-700">{invoice.generatedByName}</td>
                    <td className="px-5 py-3 text-gray-700">{new Date(invoice.generatedAt).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-center">
                      <button onClick={() => handleViewInvoice(invoice)}
                        className="text-primary hover:text-blue-700 text-sm font-medium transition-colors">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-500 mb-4">No billing records generated yet for this enterprise.</p>
          <p className="text-sm text-gray-400">Use the form above to generate your first billing record.</p>
        </div>
      )}
    </div>
  );
};

export default Invoices;
